import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Crown, GripVertical, LayoutTemplate, Lock, RotateCcw, Rows3 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { usePlan } from '@/context/PlanContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { getWebsiteLayout, resetWebsiteLayout, saveWebsiteLayout } from '@/services/websiteLayoutApi';
import {
  WEBSITE_SECTION_LABELS,
  type DesktopColumns,
  type ImagePosition,
  type SectionWidth,
  type WebsiteLayout,
  type WebsiteLayoutSection,
} from '@/types/websiteLayout';

const OptionGroup = <T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: readonly T[];
  onChange: (value: T) => void;
  label: string;
}) => (
  <div>
    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-1">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={`rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${
            value === option ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {option === 'fluid' ? 'Fullscreen' : option}
        </button>
      ))}
    </div>
  </div>
);

const SortableSection = ({
  section,
  onChange,
}: {
  section: WebsiteLayoutSection;
  onChange: (next: WebsiteLayoutSection) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.key });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.65 : 1 }}
      className="rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="mt-1 rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          aria-label={`Reorder ${WEBSITE_SECTION_LABELS[section.key]}`}
        >
          <GripVertical className="size-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center gap-2">
            <LayoutTemplate className="size-4 text-primary" />
            <h3 className="font-semibold">{WEBSITE_SECTION_LABELS[section.key]}</h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <OptionGroup<SectionWidth>
              label="Content width"
              value={section.width}
              options={['standard', 'fluid']}
              onChange={(width) => onChange({ ...section, width })}
            />
            {section.imagePosition && (
              <OptionGroup<ImagePosition>
                label="Desktop image"
                value={section.imagePosition}
                options={['left', 'right']}
                onChange={(imagePosition) => onChange({ ...section, imagePosition })}
              />
            )}
            {section.desktopColumns && (
              <OptionGroup<DesktopColumns>
                label="Desktop cards"
                value={section.desktopColumns}
                options={[3, 4, 6]}
                onChange={(desktopColumns) => onChange({ ...section, desktopColumns })}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function WebsiteLayoutEditor({ domain }: { domain: string }) {
  const { capabilities, isSuperAdmin, loading: planLoading, openUpgradeModal } = usePlan();
  const hasLayoutAccess = capabilities.websiteLayoutsAccess || isSuperAdmin;
  const [layout, setLayout] = useState<WebsiteLayout | null>(null);
  const [savedLayout, setSavedLayout] = useState<WebsiteLayout | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (planLoading) return;
    if (!hasLayoutAccess) {
      setLoading(false);
      setLayout(null);
      setSavedLayout(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    getWebsiteLayout(controller.signal)
      .then((data) => {
        setLayout(data);
        setSavedLayout(data);
      })
      .catch((error) => {
        if (error.name !== 'CanceledError') toast.error(error.response?.data?.message || 'Failed to load website layout');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [domain, hasLayoutAccess, planLoading]);

  const hasChanges = useMemo(
    () => Boolean(layout && savedLayout && (
      layout.globalWidth !== savedLayout.globalWidth
      || JSON.stringify(layout.shell) !== JSON.stringify(savedLayout.shell)
      || JSON.stringify(layout.sections) !== JSON.stringify(savedLayout.sections)
    )),
    [layout, savedLayout],
  );

  const masterWidth = useMemo<SectionWidth | 'mixed'>(() => {
    if (!layout) return 'mixed';
    const shellMatches = Object.values(layout.shell).every((item) => item.width === layout.globalWidth);
    if (shellMatches && layout.sections.every((section) => section.width === layout.globalWidth)) return layout.globalWidth;
    return 'mixed';
  }, [layout]);

  const setAllWidths = (width: SectionWidth) => {
    setLayout((current) => current
      ? {
          ...current,
          globalWidth: width,
          shell: {
            navigation: { width },
            footer: { width },
            audioBar: { width },
          },
          sections: current.sections.map((section) => ({ ...section, width })),
        }
      : current);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!layout || !over || active.id === over.id) return;
    const oldIndex = layout.sections.findIndex((section) => section.key === active.id);
    const newIndex = layout.sections.findIndex((section) => section.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setLayout({ ...layout, sections: arrayMove(layout.sections, oldIndex, newIndex) });
  };

  const updateSection = (next: WebsiteLayoutSection) => {
    setLayout((current) => current
      ? { ...current, sections: current.sections.map((section) => section.key === next.key ? next : section) }
      : current);
  };

  const handleSave = async () => {
    if (!layout) return;
    try {
      setSaving(true);
      const saved = await saveWebsiteLayout(layout);
      setLayout(saved);
      setSavedLayout(saved);
      toast.success('Website layout updated');
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.error('This layout changed in another session. Reload the page before saving again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to save website layout');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefault = async () => {
    if (!layout) return;
    try {
      setResetting(true);
      const reset = await resetWebsiteLayout(layout.revision);
      setLayout(reset);
      setSavedLayout(reset);
      toast.success('Website layout reset to default');
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.error('This layout changed in another session. Reload the page before resetting it.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to reset website layout');
      }
    } finally {
      setResetting(false);
    }
  };

  if (planLoading || loading) return <PageLoader label="Loading website layout..." />;
  if (!hasLayoutAccess) {
    return (
      <div className="max-w-4xl border-y border-border bg-muted/25 px-5 py-12 sm:px-8">
        <div className="mx-auto flex max-w-lg flex-col items-center text-center">
          <div className="mb-5 flex size-14 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            <Lock className="size-6" />
          </div>
          <h3 className="text-xl font-semibold text-foreground">Website layouts require Pro Max</h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Upgrade to arrange sections, change content widths, adjust desktop card counts, and customize the website frame.
          </p>
          <Button className="mt-6" onClick={() => openUpgradeModal('websiteLayoutsAccess')}>
            <Crown className="mr-2 size-4" />
            Upgrade to Pro Max
          </Button>
        </div>
      </div>
    );
  }
  if (!layout) return <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">Website layout could not be loaded.</div>;

  return (
    <div className="max-w-4xl space-y-5">
      <div className="rounded-xl border border-border bg-secondary/30 p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <Rows3 className="mt-0.5 size-5 shrink-0 text-primary" />
            <div>
              <h3 className="font-semibold">Arrange your website</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Drag sections into order and choose how each section uses the available space. Card counts apply on wide screens; mobile remains responsive.
              </p>
            </div>
          </div>

          <div className="shrink-0 sm:text-right">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              All section widths{masterWidth === 'mixed' ? ' · Mixed' : ''}
            </div>
            <div className="inline-flex rounded-lg border border-border bg-card/70 p-1">
              {(['standard', 'fluid'] as const).map((width) => (
                <button
                  key={width}
                  type="button"
                  onClick={() => setAllWidths(width)}
                  className={`rounded-md px-4 py-1.5 text-xs font-semibold capitalize transition-colors ${
                    masterWidth === width
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`}
                >
                  {width === 'fluid' ? 'Fullscreen' : width}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="mb-4">
          <h3 className="font-semibold">Website frame</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            These elements stay fixed in their normal positions and cannot be reordered.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {([
            ['navigation', 'Navigation'],
            ['footer', 'Footer'],
            ['audioBar', 'Audio streaming bar'],
          ] as const).map(([key, label]) => (
            <OptionGroup<SectionWidth>
              key={key}
              label={label}
              value={layout.shell[key].width}
              options={['standard', 'fluid']}
              onChange={(width) => setLayout((current) => current ? {
                ...current,
                shell: { ...current.shell, [key]: { width } },
              } : current)}
            />
          ))}
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={layout.sections.map((section) => section.key)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {layout.sections.map((section) => (
              <SortableSection key={section.key} section={section} onChange={updateSection} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="sticky bottom-4 flex gap-2 rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur">
        <Button onClick={handleSave} disabled={!hasChanges || resetting} loading={saving} loadingText="Saving...">Save Layout</Button>
        <Button variant="outline" disabled={!hasChanges || saving || resetting} onClick={() => setLayout(savedLayout)}>Reset changes</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="ml-auto" disabled={saving || resetting}>
              <RotateCcw className="mr-2 size-4" />
              Reset to default
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset website layout?</AlertDialogTitle>
              <AlertDialogDescription>
                This restores the original section order, widths, image positions, card counts, and website frame settings. The reset is saved immediately.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleResetDefault}>Reset to default</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
